/**
 * The configuration entry point.
 *
 * Everything outside `$lib/config` imports from this module and nothing else. That
 * keeps the raw data files (`maps.ts`, `ui.ts`, …) as the single place decisions are
 * authored, and this module as the single place they are validated and read.
 *
 * The exports fall into three groups:
 *
 * 1. `config`, the validated configuration, with defaults applied.
 * 2. Selectors, derived views (visible maps, the default map, groups, resolved
 *    features) so components never re-implement a filtering rule.
 * 3. Diagnostics, validation issues and environment warnings, surfaced in the UI
 *    and in the console rather than swallowed.
 */
import { mapCategories, maps } from './maps';
import { markerCategories } from './markers';
import { players } from './players';
import { servers, DEVELOPMENT_BLUEMAP_ORIGIN } from './servers';
import { site } from './site';
import { publicEnv } from './env';
import { ui } from './ui';
import { parseConfig, reportIssues } from './validate';
import type { ConfigIssue } from './validate';

import {
	PLAYER_SOURCE_FALLBACK,
	UI_FEATURE_DEFAULTS,
	UI_FEATURE_KEYS
} from './schema';
import type {
	BlueMapServer,
	MapCategory,
	MapConfig,
	MarkerCategory,
	ResolvedConfig,
	UiFeatureKey
} from './schema';

/* -------------------------------------------------------------------------- */
/* Resolution                                                                 */
/* -------------------------------------------------------------------------- */

const result = parseConfig({ site, servers, mapCategories, maps, markerCategories, players, ui });

/**
 * Deployment warnings that are not configuration errors.
 *
 * Falling back to a local BlueMap origin is the right default for development and a
 * likely mistake in production, so it is reported loudly rather than assumed either
 * way.
 */
const environmentIssues: ConfigIssue[] = [];
if (!publicEnv.bluemapBaseUrl) {
	environmentIssues.push({
		path: 'env.PUBLIC_BLUEMAP_BASE_URL',
		message: `not set, the "main" BlueMap server falls back to ${DEVELOPMENT_BLUEMAP_ORIGIN}. Set it for any real deployment.`,
		severity: 'warning'
	});
}
/** The configured player source, with a safe reading when none was declared. */
export const playerSource = result.config.players.source ?? PLAYER_SOURCE_FALLBACK;

if (playerSource.kind === 'api' && !publicEnv.apiBaseUrl) {
	environmentIssues.push({
		path: 'env.PUBLIC_API_BASE_URL',
		message:
			'player source is "api" but no API base URL is configured; the player overlay will report as unavailable.',
		severity: 'warning'
	});
}

/** The validated configuration. Safe to read even when validation reported issues. */
export const config: ResolvedConfig = result.config;

/** Everything wrong or suspicious, ready to render or log. */
export const configIssues: ConfigIssue[] = [...result.issues, ...environmentIssues];

/** True when no usable BlueMap server exists, so no map can be rendered at all. */
export const configFatal: boolean = result.fatal;

/** True when nothing at all was reported, the happy path. */
export const configIsClean: boolean = configIssues.length === 0;

if (publicEnv.debug || configIssues.some((issue) => issue.severity === 'error')) {
	reportIssues(configIssues);
}

/* -------------------------------------------------------------------------- */
/* Selectors                                                                  */
/* -------------------------------------------------------------------------- */

/** Stable ordering: explicit `order` first, then name, so results never jitter. */
function byOrder<T extends { order: number }>(a: T, b: T): number {
	return a.order - b.order;
}

function sortMaps(list: MapConfig[]): MapConfig[] {
	return [...list].sort((a, b) => byOrder(a, b) || a.name.localeCompare(b.name));
}

/** Every map that is switched on, including ones hidden from the selector. */
export const enabledMaps: MapConfig[] = sortMaps(config.maps.filter((map) => map.enabled));

/**
 * Maps offered in the selector: enabled, selectable and not hidden.
 *
 * `visible: false` is how a map stays reachable by direct URL, a staging or
 * deep-link-only world, without cluttering the list.
 */
export const selectableMaps: MapConfig[] = enabledMaps.filter(
	(map) => map.selectable && map.visible
);

const defaultMapCandidate: MapConfig | undefined =
	enabledMaps.find((map) => map.default && map.selectable) ??
	selectableMaps[0] ??
	enabledMaps[0];

/** The map to open when the URL does not name one. `undefined` only if no map exists. */
export const defaultMap: MapConfig | undefined = defaultMapCandidate;

export const defaultMapId: string | undefined = defaultMap?.id;

/**
 * Looks a map up by URL slug, which is what routing uses. Returns `undefined` for
 * unknown *and* for disabled slugs, so neither is silently substituted for another map.
 */
export function findMapBySlug(slug: string | undefined): MapConfig | undefined {
	if (!slug) return undefined;
	return enabledMaps.find((map) => map.slug === slug);
}

/**
 * Looks a map up by its internal id.
 *
 * Only the legacy `/map/<id>` redirect uses this: the URL segment is a slug, and the two
 * differ wherever a map was given a shorter one.
 */
export function findMapById(id: string | undefined): MapConfig | undefined {
	if (!id) return undefined;
	return enabledMaps.find((map) => map.id === id);
}

/** Maps that are dimensions of the same world, excluding the current one. */
export function siblingDimensions(map: MapConfig): MapConfig[] {
	if (!map.group) return [];
	return enabledMaps.filter(
		(candidate) => candidate.group === map.group && candidate.id !== map.id
	);
}

/** Groups that actually contain a selectable map, in configured order. */
export function visibleCategories(): MapCategory[] {
	const used = new Set(selectableMaps.map((map) => map.category).filter(Boolean));
	return [...config.mapCategories].filter((c) => used.has(c.id)).sort(byOrder);
}

export interface MapGroup {
	category: MapCategory;
	maps: MapConfig[];
}

/**
 * The selector's data model: categories in order, each with its maps, followed by
 * an uncategorised bucket. Computed from the map list, so a new category appears
 * as soon as a map references it.
 */
export function groupedMaps(): MapGroup[] {
	const groups = visibleCategories().map((category) => ({
		category,
		maps: selectableMaps.filter((map) => map.category === category.id)
	}));

	const uncategorised = selectableMaps.filter(
		(map) => !map.category || !config.mapCategories.some((c) => c.id === map.category)
	);

	if (uncategorised.length > 0) {
		groups.push({
			category: { id: '__uncategorised', name: 'Other', order: Number.MAX_SAFE_INTEGER },
			maps: uncategorised
		});
	}

	return groups;
}

/* -------------------------------------------------------------------------- */
/* Feature resolution                                                         */
/* -------------------------------------------------------------------------- */

/**
 * The effective feature set for a map.
 *
 * Per-map overrides *narrow* the site-wide features, they never widen them: a map
 * cannot switch on a feature an operator disabled globally. This is what makes
 * `ui.features` a trustworthy capability ceiling.
 */
export function resolveFeatures(map?: MapConfig): Record<UiFeatureKey, boolean> {
	const base = config.ui.features ?? UI_FEATURE_DEFAULTS;
	const overrides = map?.features;

	const resolved = {} as Record<UiFeatureKey, boolean>;
	for (const key of UI_FEATURE_KEYS) {
		const siteWide = base[key] ?? UI_FEATURE_DEFAULTS[key];
		const perMap = overrides?.[key];
		resolved[key] = siteWide && (perMap ?? true);
	}
	return resolved;
}

/* -------------------------------------------------------------------------- */
/* BlueMap target resolution                                                  */
/* -------------------------------------------------------------------------- */

export interface ResolvedMapTarget {
	serverId: string;
	serverName: string;
	/** Origin of the BlueMap instance serving this map. */
	baseUrl: string;
	mapId: string;
	worldId?: string;
	/** BlueMap's own web app, when the server declares one. */
	webappUrl?: string;
}

/** The server used by maps that do not name one. */
export function defaultServer(): BlueMapServer | undefined {
	return config.servers.find((server) => server.default) ?? config.servers[0];
}

/**
 * Turns a map's `bluemap` block into a concrete origin.
 *
 * This is the only place a BlueMap URL is assembled, which is what allows maps on
 * different BlueMap instances to coexist without any component knowing about it.
 */
export function resolveMapTarget(map: MapConfig): ResolvedMapTarget | undefined {
	const { server: serverId, baseUrl, mapId, worldId } = map.bluemap;

	if (baseUrl) {
		// An explicit origin wins. It still borrows a server's display metadata when
		// one is named, so the UI has something to show.
		const named = serverId ? config.servers.find((s) => s.id === serverId) : undefined;
		return {
			serverId: named?.id ?? serverId ?? 'custom',
			serverName: named?.name ?? 'Custom BlueMap',
			baseUrl,
			mapId,
			worldId,
			webappUrl: named?.webappUrl
		};
	}

	const server = serverId
		? config.servers.find((s) => s.id === serverId)
		: defaultServer();

	if (!server) return undefined;

	return {
		serverId: server.id,
		serverName: server.name,
		baseUrl: server.baseUrl,
		mapId,
		worldId,
		webappUrl: server.webappUrl
	};
}

/** True when a server is still pointing at the development fallback origin. */
export function usesDevelopmentOrigin(serverId: string): boolean {
	const server = config.servers.find((s) => s.id === serverId);
	return server?.baseUrl === DEVELOPMENT_BLUEMAP_ORIGIN;
}

/* -------------------------------------------------------------------------- */
/* Marker selectors                                                           */
/* -------------------------------------------------------------------------- */

/** Marker categories available for the marker panel, in configured order. */
export const activeMarkerCategories: MarkerCategory[] = config.markerCategories
	.filter((category) => category.enabled)
	.sort(byOrder);

/** Categories a user may toggle, i.e. everything enabled. */
export function markerCategoryById(id: string): MarkerCategory | undefined {
	return activeMarkerCategories.find((category) => category.id === id);
}

/** True when the plugin API is needed but not configured for any category. */
export function categoryNeedsApi(category: MarkerCategory): boolean {
	return category.source === 'api';
}

/* -------------------------------------------------------------------------- */
/* Re-exports                                                                 */
/* -------------------------------------------------------------------------- */

export { UI_FEATURE_DEFAULTS, UI_FEATURE_KEYS } from './schema';
export type {
	BlueMapServer,
	MapCategory,
	MapConfig,
	MarkerCategory,
	ResolvedConfig,
	UiFeatureKey
} from './schema';
export type { ConfigIssue } from './validate';
